import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import { IdentificationStatus } from "../../Base/AdoptionConstants";
import useAlerts from "../../Base/AlertProvider";
import { DocumentType, uploadRegistrationDocument } from "../../Base/Services/RegistrationDocumentService";
import { updateRegistrationIdentificationStatus } from "../../Base/Services/RegistrationService";
import VerifyIdentityForm, { ID_DOCUMENT_NAME_1, ID_DOCUMENT_NAME_2 } from "../RegistrationForm/VerifyIdentityForm";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  responsiveDialog: {
    [theme.breakpoints.down("xs")]: {
      margin: 0,
      maxWidth: "100%",
      width: "100%",
      height: "100%",
      maxHeight: "none",
      borderRadius: 0,
    },
  },
  compactTitle: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactActions: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactContent: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
}));

export default function ViewUploadIdentificationDocumentsDialog({ open, onClose, registration, ariaLabel, ...props }) {
  const classes = useStyles();
  const { addSuccessAlert, addErrorAlert } = useAlerts();

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
      }}
      maxWidth="sm"
      aria-labelledby="identity-verification-dialog-title"
      aria-describedby="identity-verification-dialog-description"
      classes={{ paper: classes.responsiveDialog }}
      {...props}
    >
      {registration && (
        <Formik
          initialValues={{
            identificationDocuments: "",
            copyOfId1: "",
            copyOfId2: "",
            verifyIdentityOption:
              registration.identificationStatus === IdentificationStatus.PENDING_REVIEW_MAIL.value
                ? "mail"
                : registration.identificationStatus === IdentificationStatus.PENDING_REVIEW_UPLOAD.value
                ? "upload"
                : "",
          }}
          validationSchema={Yup.object()
            .shape({
              verifyIdentityOption: Yup.string().required("Please select an option"),
              copyOfId1: Yup.mixed().fileIsImageOrPdf(),
              copyOfId2: Yup.mixed().fileIsImageOrPdf(),
            })
            .test(
              "at-least-one-id-required",
              "Please select a file to upload a copy of your ID.",
              function (formValues) {
                if (
                  formValues.verifyIdentityOption === "upload" &&
                  !Boolean(formValues.copyOfId1) &&
                  !Boolean(formValues.copyOfId2)
                ) {
                  return this.createError({
                    message: "Please select a file to upload a copy of your ID.",
                    path: "identificationDocuments",
                  });
                }
                return true;
              }
            )}
          onSubmit={(formValues) => {
            const identificationPromises = [];
            if (formValues.verifyIdentityOption === "mail") {
              identificationPromises.push(
                updateRegistrationIdentificationStatus(
                  registration.registrationId,
                  IdentificationStatus.PENDING_REVIEW_MAIL.value
                )
              );
            } else if (formValues.verifyIdentityOption === "upload") {
              // Only upload newly selected file (which contains a file size)
              if (formValues.copyOfId1?.size) {
                identificationPromises.push(
                  uploadRegistrationDocument(
                    registration.registrationId,
                    formValues.copyOfId1,
                    DocumentType.IDENTIFICATION,
                    ID_DOCUMENT_NAME_1
                  )
                );
              }
              // Only upload newly selected file (which contains a file size)
              if (formValues.copyOfId2?.size) {
                identificationPromises.push(
                  uploadRegistrationDocument(
                    registration.registrationId,
                    formValues.copyOfId2,
                    DocumentType.IDENTIFICATION,
                    ID_DOCUMENT_NAME_2
                  )
                );
              }
            }

            return Promise.all(identificationPromises)
              .then((responses) => {
                addSuccessAlert("Changes saved");
                onClose(responses);
              })
              .catch((e) => {
                console.error(e);
                addErrorAlert("Error saving changes - please try again");
              });
          }}
        >
          {(formik) => (
            <Form onSubmit={formik.handleSubmit} className={classes.form}>
              <DialogTitle id="identity-verification-dialog-title" className={classes.compactTitle}>
                <span style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Identity Verification</span>
                  <IconButton size="small" color="default" onClick={onClose} aria-label="close-dialog">
                    <Close />
                  </IconButton>
                </span>
              </DialogTitle>
              <DialogContent id="identity-verification-dialog-description" className={classes.compactContent}>
                <VerifyIdentityForm registrationId={registration.registrationId} />
              </DialogContent>
              <DialogActions className={classes.compactActions}>
                <Button type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={formik.isSubmitting}
                  onClick={formik.handleSubmit}
                >
                  {formik.isSubmitting && <CircularProgress color="inherit" size={16} style={{ marginRight: 12 }} />}{" "}
                  Save Changes
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
}
